import GoogleMap, { type GoogleMapsProps } from "components/GoogleMap/googlemap";
import { GooglePlaceInputValue } from "components/ValidatedFields/GooglePlaceInput";
import React, { useEffect } from "react";
import { formatDistance } from "utilities/geometry";

interface Props extends GoogleMapsProps {
  height: number
  route: server.dto.GoogleDirections
  startPlace: GooglePlaceInputValue
  endPlace: GooglePlaceInputValue
}

const formatTooltip = (place: GooglePlaceInputValue) => {
  if (!place) {
    return "";
  }

  return `<div style="font-size: 14px; font-weight: 500">${place.name}</div>` +
    `<div style="font-size: 14px; font-weight: 400">${place.address}</div>`;
}

export const GoogleMapRoute = (props: Props) => {
  const {
    height,
    route,
    startPlace,
    endPlace,
    ...rest
  } = props;

  const mapRef = React.useRef<GoogleMap>(null);
  const hasAnything = startPlace?.geo || endPlace?.geo;

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.zoomToFitAllowZoomIn();
      }, 50);
    }
  }, [route, startPlace?.id, endPlace?.id]);

  if (!hasAnything) {
    return null;
  }

  const startInfoWindow = new google.maps.InfoWindow({
    content: formatTooltip(startPlace),
  });

  const endInfoWindow = new google.maps.InfoWindow({
    content: formatTooltip(endPlace),
  });

  const wrapperHeight = height + 56; // 56px for the distance/duration box

  const coordinates = [];

  if (startPlace?.geo) {
    coordinates.push({
      position: { lat: startPlace.geo.lat, lng: startPlace.geo.lng },
      pin: {
        type: "groundTransport",
        content: "A",
      },
      clickable: true,
      onLoaded(_, marker) {
        marker.addListener("click", () => {
          startInfoWindow.open(marker.map, marker as any);
        });
      },
    });
  }

  if (endPlace?.geo) {
    coordinates.push({
      position: { lat: endPlace.geo.lat, lng: endPlace.geo.lng },
      pin: {
        type: "groundTransport",
        content: "B",
      },
      clickable: true,
      onLoaded(_, marker) {
        marker.addListener("click", () => {
          endInfoWindow.open(marker.map, marker as any);
        });
      },
    });
  }

  const polylines = route?.polyline ? [{
    id: route.polyline,
    polyline: route.polyline,
    hexColor: route.hexColor,
  }] : [];

  return <div className="radius" style={{
    height: wrapperHeight,
    minHeight: wrapperHeight,
    overflow: "hidden",
  }}>
    <div style={{ height, minHeight: height }}>
      <GoogleMap
        ref={mapRef}
        autoFitBounds
        polylines={polylines}
        coordinates={coordinates}
        mapOptions={{
          center: getCenter(startPlace, endPlace),
          zoom: 10,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: false,
          mapTypeControl: false,
        }}
        {...rest}
      />
    </div>
    {(props.route && props.route.distanceInMeters !== 0) && <div className="card-floating-bg padding-top-large padding-bottom-large padding-left padding-right">
      <div>{RESX.Flight.Distance}:&nbsp;&nbsp;{formatDistance(props.route.distanceInMeters)}</div>
    </div>}
  </div>
}

const getCenter = (startPlace: GooglePlaceInputValue, endPlace: GooglePlaceInputValue) => {
  if (startPlace?.geo && endPlace?.geo) {
    return {
      lat: (startPlace.geo.lat + endPlace.geo.lat) / 2,
      lng: (startPlace.geo.lng + endPlace.geo.lng) / 2
    };
  }

  if (startPlace?.geo) {
    return {
      lat: startPlace.geo.lat,
      lng: startPlace.geo.lng
    };
  }

  if (endPlace?.geo) {
    return {
      lat: endPlace.geo.lat,
      lng: endPlace.geo.lng
    };
  }

  return { lat: 0, lng: 0 };
}